import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Activity, User, Mail, Lock, Shield, AlertCircle, ArrowLeft, CheckSquare, Square, ChevronDown, X, Edit, Trash2, Eye, EyeOff, Plus, Search } from 'lucide-react';
import { BASE_URL } from '../config';

export default function AdminRegister({ onBack }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Admin list state
  const [admins, setAdmins] = useState([]);
  const [fetchingAdmins, setFetchingAdmins] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdminList, setShowAdminList] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Dropdown states
  const [systemFunctionsExpanded, setSystemFunctionsExpanded] = useState(false);
  const [sidebarAccessExpanded, setSidebarAccessExpanded] = useState(false);

  // System Functions Access
  const [systemFunctions, setSystemFunctions] = useState({
    add_admin: false,
    setup: false,
    notifications: false,
    discounts: false,
  });

  // Sidebar Access
  const [sidebarAccess, setSidebarAccess] = useState({
    dashboard: true, // Always enabled
    hospitals: false,
    clinics: false,
    doctors: false,
    users: false,
    packages: false,
    departments: false,
    seo: false,
  });

  const systemFunctionsList = [
    { key: 'add_admin', label: 'Add Admin', description: 'Create new administrator accounts' },
    { key: 'setup', label: 'System Setup', description: 'Configure system settings' },
    { key: 'notifications', label: 'Notifications', description: 'Manage system notifications' },
    { key: 'discounts', label: 'Discount Management', description: 'Manage package discounts' },
  ];

  const sidebarFunctionsList = [
    { key: 'dashboard', label: 'Dashboard', description: 'Main dashboard view', disabled: true },
    { key: 'hospitals', label: 'Hospitals', description: 'Manage hospitals' },
    { key: 'clinics', label: 'Clinics', description: 'Manage clinics' },
    { key: 'doctors', label: 'Doctors', description: 'Manage doctors' },
    { key: 'users', label: 'Users', description: 'Manage users' },
    { key: 'packages', label: 'Packages', description: 'Manage packages' },
    { key: 'departments', label: 'Departments', description: 'Manage departments' },
    { key: 'seo', label: 'SEO Management', description: 'Manage SEO content' },
  ];

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Fetch admins list
  const fetchAdmins = async () => {
    try {
      setFetchingAdmins(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      const response = await axios.get(`${BASE_URL}/api/superadmin/admins/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('✅ Admins fetched:', response.data);
      setAdmins(response.data.admins || []);
    } catch (err) {
      console.error('❌ Failed to fetch admins:', err);
      showNotification('Failed to fetch admins list', 'error');
    } finally {
      setFetchingAdmins(false);
    }
  };

  // Edit admin
  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setUsername(admin.username);
    setEmail(admin.email);
    setPassword(''); // Don't populate password for security
    setSystemFunctions(admin.system_functions || {
      add_admin: false,
      setup: false,
      notifications: false,
      discounts: false,
    });
    setSidebarAccess(admin.sidebar_access || {
      dashboard: true,
      hospitals: false,
      clinics: false,
      doctors: false,
      users: false,
      packages: false,
      departments: false,
      seo: false,
    });
    setShowForm(true);
    setShowAdminList(false);
  };

  // Delete admin
  const handleDeleteAdmin = async (adminId, adminUsername) => {
    if (!window.confirm(`Are you sure you want to delete admin "${adminUsername}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        showNotification('Authentication token not found. Please login again.', 'error');
        return;
      }

      await axios.delete(`${BASE_URL}/api/superadmin/admins/${adminId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      showNotification('Admin deleted successfully');
      fetchAdmins(); // Refresh list
    } catch (err) {
      console.error('❌ Failed to delete admin:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to delete admin';
      showNotification(errorMessage, 'error');
    }
  };

  // Cancel form
  const handleCancelForm = () => {
    setEditingAdmin(null);
    setUsername('');
    setEmail('');
    setPassword('');
    setSystemFunctions({
      add_admin: false,
      setup: false,
      notifications: false,
      discounts: false,
    });
    setSidebarAccess({
      dashboard: true,
      hospitals: false,
      clinics: false,
      doctors: false,
      users: false,
      packages: false,
      departments: false,
      seo: false,
    });
    setShowForm(false);
    setShowAdminList(true);
  };

  const toggleSystemFunction = (key) => {
    setSystemFunctions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleSidebarAccess = (key) => {
    if (key === 'dashboard') return; // Dashboard always enabled
    setSidebarAccess(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Select All functions
  const selectAllSystemFunctions = () => {
    const allSelected = Object.values(systemFunctions).every(val => val === true);
    const newState = {};
    Object.keys(systemFunctions).forEach(key => {
      newState[key] = !allSelected;
    });
    setSystemFunctions(newState);
  };

  const selectAllSidebarAccess = () => {
    const allSelected = Object.keys(sidebarAccess)
      .filter(key => key !== 'dashboard')
      .every(key => sidebarAccess[key] === true);
    
    const newState = { dashboard: true }; // Dashboard always enabled
    Object.keys(sidebarAccess).forEach(key => {
      if (key !== 'dashboard') {
        newState[key] = !allSelected;
      }
    });
    setSidebarAccess(newState);
  };

  // Check if all are selected
  const allSystemFunctionsSelected = Object.values(systemFunctions).every(val => val === true);
  const allSidebarAccessSelected = Object.keys(sidebarAccess)
    .filter(key => key !== 'dashboard')
    .every(key => sidebarAccess[key] === true);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validation: Check if at least one system function is selected
    const hasSystemFunction = Object.values(systemFunctions).some(val => val === true);
    
    // Validation: Check if at least one sidebar access is selected (excluding dashboard which is always true)
    const hasSidebarAccess = Object.keys(sidebarAccess)
      .filter(key => key !== 'dashboard')
      .some(key => sidebarAccess[key] === true);
    
    if (!hasSystemFunction && !hasSidebarAccess) {
      showNotification('Please select at least one System Function or Sidebar Access permission for the admin', 'error');
      setLoading(false);
      return;
    }
    
    console.log('🔍 Attempting to register/update admin with data:', {
      username,
      email,
      password: editingAdmin && !password ? 'Not changing' : '***',
      role: 5,
      systemFunctions,
      sidebarAccess,
      isEditing: !!editingAdmin
    });
    
    try {
      const requestData = {
        username,
        email,
        role: 5,
        system_functions: systemFunctions,
        sidebar_access: sidebarAccess,
      };

      // Only include password if it's provided (for create or update)
      if (password) {
        requestData.password = password;
      }

      const token = localStorage.getItem('accessToken');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      let response;
      if (editingAdmin) {
        // Update existing admin
        response = await axios.put(
          `${BASE_URL}/api/superadmin/admins/${editingAdmin.id}/`,
          requestData,
          config
        );
        console.log('✅ Admin updated successfully:', response.data);
        showNotification('Admin updated successfully!');
      } else {
        // Create new admin
        response = await axios.post(
          `${BASE_URL}/api/superadmin/register/`,
          requestData,
          config
        );
        console.log('✅ Admin created successfully:', response.data);
        showNotification(`Admin registered successfully! User ID: ${response.data.user_id}`);
      }
      
      // Clear form and refresh list
      handleCancelForm();
      fetchAdmins();
      
    } catch (err) {
      console.error('❌ Registration/Update failed:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Operation failed';
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: '5', label: 'Admin', icon: Shield },
    { value: '4', label: 'Hospital', icon: Activity },
    { value: '3', label: 'Clinic', icon: Activity },
    { value: '2', label: 'Doctor', icon: User },
    { value: '1', label: 'User', icon: User },
  ];

  // Fetch admins on component mount
  useEffect(() => {
    fetchAdmins();
  }, []);

  // Filter admins based on search
  const filteredAdmins = admins.filter(admin => 
    admin.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, x: 300, scale: 0.3 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 300, scale: 0.3 }}
          className={`fixed top-4 right-4 z-50 rounded-lg shadow-lg overflow-hidden min-w-[300px] max-w-[400px] ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
        >
          <div className="p-4 flex items-center gap-3">
            {notification.type === 'success' ? (
              <CheckSquare className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="flex-1">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Progress bar */}
          <motion.div
            initial={{ width: "100%" }}
            animate={{ width: "0%" }}
            transition={{ duration: 3, ease: "linear" }}
            className={`h-1 ${
              notification.type === 'success' 
                ? 'bg-green-400' 
                : 'bg-red-400'
            }`}
          />
        </motion.div>
      )}

      {/* Back Button */}
      {onBack && (
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-medical-600 hover:bg-medical-50 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-medium">Back</span>
        </motion.button>
      )}

      {/* Header with Animation */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="relative"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <motion.div 
              className="relative flex items-center justify-center w-16 h-16 bg-gradient-to-br from-medical-500 to-medical-600 rounded-xl shadow-lg"
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Shield className="w-8 h-8 text-slate-900" />
              <motion.div
                className="absolute inset-0 bg-medical-400 rounded-xl"
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0, 0.5]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800">Admin Management</h1>
              <p className="text-slate-600">Manage administrator accounts and permissions</p>
            </div>
          </div>
          {showAdminList && (
            <button
              onClick={() => {
                setShowForm(true);
                setShowAdminList(false);
              }}
              className="bg-medical-500 text-white px-6 py-3 rounded-lg hover:bg-medical-600 transition-colors flex items-center gap-2 shadow-lg"
            >
              <Plus className="w-5 h-5" />
              Add New Admin
            </button>
          )}
        </div>
      </motion.div>

      {/* Admin List View */}
      {showAdminList && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden"
        >
          {/* Search Bar */}
          <div className="p-6 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search admins by username or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500 outline-none transition-all"
              />
            </div>
          </div>

          {/* Admins Table */}
          <div className="overflow-x-auto">
            {fetchingAdmins ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500"></div>
              </div>
            ) : filteredAdmins.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No admins found</h3>
                <p className="text-slate-600 mb-4">
                  {searchTerm ? 'Try adjusting your search terms' : 'Get started by creating your first admin account'}
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-6 font-medium text-slate-900">Username</th>
                    <th className="text-left py-3 px-6 font-medium text-slate-900">Email</th>
                    <th className="text-left py-3 px-6 font-medium text-slate-900">System Functions</th>
                    <th className="text-left py-3 px-6 font-medium text-slate-900">Sidebar Access</th>
                    <th className="text-left py-3 px-6 font-medium text-slate-900">Created</th>
                    <th className="text-left py-3 px-6 font-medium text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredAdmins.map((admin) => (
                    <motion.tr
                      key={admin.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-medical-600" />
                          <span className="font-medium text-slate-900">{admin.username}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-600">{admin.email}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-wrap gap-1">
                          {admin.system_functions && Object.entries(admin.system_functions)
                            .filter(([key, value]) => value === true)
                            .map(([key]) => (
                              <span
                                key={key}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {systemFunctionsList.find(f => f.key === key)?.label || key}
                              </span>
                            ))}
                          {(!admin.system_functions || Object.values(admin.system_functions).every(v => !v)) && (
                            <span className="text-xs text-slate-400">None</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-wrap gap-1">
                          {admin.sidebar_access && Object.entries(admin.sidebar_access)
                            .filter(([key, value]) => value === true && key !== 'dashboard')
                            .map(([key]) => (
                              <span
                                key={key}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                              >
                                {sidebarFunctionsList.find(f => f.key === key)?.label || key}
                              </span>
                            ))}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm text-slate-600">
                          {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditAdmin(admin)}
                            className="text-blue-400 hover:text-blue-600 transition-colors p-2 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAdmin(admin.id, admin.username)}
                            className="text-red-400 hover:text-red-600 transition-colors p-2 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </motion.div>
      )}

      {/* Register/Edit Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-medical-500 to-medical-600 px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <User className="w-5 h-5" />
              {editingAdmin ? 'Edit Admin Account' : 'Create New Admin Account'}
            </h3>
            <button
              onClick={handleCancelForm}
              className="text-slate-900 hover:text-slate-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        
        <div className="p-6">

          <form onSubmit={handleRegister} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Username Input */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4 text-medical-600" />
                  Username *
                </label>
                <input
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500 outline-none transition-all hover:border-slate-300"
                />
              </motion.div>

              {/* Email Input */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-medical-600" />
                  Email Address *
                </label>
                <input
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500 outline-none transition-all hover:border-slate-300"
                />
              </motion.div>

              {/* Password Input */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-medical-600" />
                  Password {editingAdmin ? '(Leave blank to keep current)' : '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required={!editingAdmin}
                    className="w-full px-4 py-2.5 pr-10 border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500 outline-none transition-all hover:border-slate-300"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </motion.div>
            </div>

            {/* System Functions Access - Collapsible */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="border-2 border-slate-200 rounded-lg overflow-hidden"
            >
              {/* Header - Clickable */}
              <div
                onClick={() => setSystemFunctionsExpanded(!systemFunctionsExpanded)}
                className="flex items-center justify-between p-5 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-medical-600" />
                  <h4 className="text-base font-semibold text-slate-800">System Functions Access</h4>
                </div>
                <motion.div
                  animate={{ rotate: systemFunctionsExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="w-5 h-5 text-slate-600" />
                </motion.div>
              </div>

              {/* Collapsible Content */}
              <motion.div
                initial={false}
                animate={{
                  height: systemFunctionsExpanded ? 'auto' : 0,
                  opacity: systemFunctionsExpanded ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-5 pt-3">
                  {/* Select All Checkbox - Top Left */}
                  <div className="flex items-center justify-between mb-3">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={selectAllSystemFunctions}
                    >
                      {allSystemFunctionsSelected ? (
                        <CheckSquare className="w-4 h-4 text-medical-600" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400" />
                      )}
                      <span className="text-xs font-medium text-slate-700">Select All</span>
                    </motion.div>
                    <p className="text-xs text-slate-500">Select which system functions this admin can access</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {systemFunctionsList.map((func) => (
                      <motion.div
                        key={func.key}
                        whileHover={{ scale: 1.02 }}
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          systemFunctions[func.key]
                            ? 'border-medical-500 bg-medical-50'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        onClick={() => toggleSystemFunction(func.key)}
                      >
                        <div className="mt-0.5">
                          {systemFunctions[func.key] ? (
                            <CheckSquare className="w-5 h-5 text-medical-600" />
                          ) : (
                            <Square className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{func.label}</p>
                          <p className="text-xs text-slate-600">{func.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Sidebar Access - Collapsible */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="border-2 border-slate-200 rounded-lg overflow-hidden"
            >
              {/* Header - Clickable */}
              <div
                onClick={() => setSidebarAccessExpanded(!sidebarAccessExpanded)}
                className="flex items-center justify-between p-5 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-medical-600" />
                  <h4 className="text-base font-semibold text-slate-800">Sidebar Menu Access</h4>
                </div>
                <motion.div
                  animate={{ rotate: sidebarAccessExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="w-5 h-5 text-slate-600" />
                </motion.div>
              </div>

              {/* Collapsible Content */}
              <motion.div
                initial={false}
                animate={{
                  height: sidebarAccessExpanded ? 'auto' : 0,
                  opacity: sidebarAccessExpanded ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-5 pt-3">
                  {/* Select All Checkbox - Top Left */}
                  <div className="flex items-center justify-between mb-3">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={selectAllSidebarAccess}
                    >
                      {allSidebarAccessSelected ? (
                        <CheckSquare className="w-4 h-4 text-medical-600" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400" />
                      )}
                      <span className="text-xs font-medium text-slate-700">Select All</span>
                    </motion.div>
                    <p className="text-xs text-slate-500">Select which sidebar menu items this admin can access</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sidebarFunctionsList.map((func) => (
                      <motion.div
                        key={func.key}
                        whileHover={func.disabled ? {} : { scale: 1.02 }}
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all ${
                          func.disabled
                            ? 'border-slate-300 bg-slate-100 cursor-not-allowed opacity-75'
                            : sidebarAccess[func.key]
                            ? 'border-medical-500 bg-medical-50 cursor-pointer'
                            : 'border-slate-200 bg-white hover:border-slate-300 cursor-pointer'
                        }`}
                        onClick={() => toggleSidebarAccess(func.key)}
                      >
                        <div className="mt-0.5">
                          {sidebarAccess[func.key] ? (
                            <CheckSquare className={`w-5 h-5 ${func.disabled ? 'text-slate-500' : 'text-medical-600'}`} />
                          ) : (
                            <Square className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">
                            {func.label}
                            {func.disabled && <span className="text-xs text-slate-500 ml-2">(Always enabled)</span>}
                          </p>
                          <p className="text-xs text-slate-600">{func.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Register Button */}
            <motion.div 
              className="flex justify-end gap-3 pt-4 border-t border-slate-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <button
                type="button"
                onClick={handleCancelForm}
                className="px-8 py-3 text-slate-600 hover:text-slate-800 font-semibold rounded-lg transition-all border-2 border-slate-200 hover:border-slate-300"
              >
                Cancel
              </button>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3 bg-gradient-to-r from-medical-500 to-medical-600 hover:from-medical-600 hover:to-medical-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl"
              >
                <Shield className="w-5 h-5" />
                {loading ? (
                  <>
                    <span className="animate-pulse">{editingAdmin ? 'Updating...' : 'Creating Account...'}</span>
                  </>
                ) : (
                  editingAdmin ? 'Update Admin Account' : 'Create Admin Account'
                )}
              </motion.button>
            </motion.div>
          </form>
        </div>
      </motion.div>
      )}
    </div>
  );
}